import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
// react-pdf ships the TextLayer/AnnotationLayer CSS separately. Without these
// the text layer (enabled via renderTextLayer below) renders as opaque,
// statically-positioned text BELOW the canvas instead of an invisible overlay
// absolutely-positioned over it — i.e. every PDF shows its text duplicated.
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { ChevronLeft, ChevronRight, Download, Maximize2, Minus, Plus } from "lucide-react";

import { prompt } from "../ando/dialog";
import { cn } from "../lib/cn";
import { Button } from "../primitives/button";
import type { EditSession } from "./EditSession";
import { fetchSessionBytes } from "./fetchSessionBytes";

/**
 * Configure the PDF.js worker once at module load. Vite/webpack resolve the
 * `new URL(...)` form at bundle time, so the worker ships next to the host
 * app without needing a global CDN — important for the air-gapped on-prem
 * deployments that don't allow `cdnjs.cloudflare.com`.
 *
 * We do the assignment at module level (not in an effect) so it runs before
 * `<Document>` mounts; otherwise the very first PDF load races a missing
 * worker and falls back to fake-worker single-threaded mode.
 */
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).href;

export interface PDFViewerInlineProps {
  session: EditSession;
  /** Optional 1-indexed page the caller wants brought into view on mount. */
  pageHint?: number;
}

interface LoadedDoc {
  numPages: number;
}

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3] as const;
const ZOOM_MIN = ZOOM_STEPS[0];
const ZOOM_MAX = ZOOM_STEPS[ZOOM_STEPS.length - 1] ?? 3;
const clampZoom = (z: number): number => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
const PAGE_GUTTER_PX = 64; // breathing room around the page inside the cream canvas

/**
 * PDFViewerInline — react-pdf based viewer mounted inside the editor pane.
 *
 * v2 upgrades (May 24 2026):
 *  - ResizeObserver-driven page width so the page never overflows the cream
 *    canvas (was clipping into the right rail on narrow viewports).
 *  - Sticky toolbar with: page nav (◀ N/M ▶), zoom (− % +), Fit, Download —
 *    matches the v1 PDF viewer affordances users expected.
 *  - Tracks the currently-visible page via IntersectionObserver so the
 *    toolbar counter is meaningful when scrolling, not just on jumps.
 */
export function PDFViewerInline({ session, pageHint }: PDFViewerInlineProps) {
  const { i18n } = useLingui();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [doc, setDoc] = useState<LoadedDoc | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // While a button/keyboard jump's smooth-scroll is in flight, ignore the
  // IntersectionObserver so it can't override the optimistic ``currentPage``
  // with an intermediate page the scroll passes through (which made ◀/▶ land
  // on the wrong page → "salta più di una pagina"). Timestamp, not boolean, so
  // overlapping jumps just extend the window.
  const suppressObserverUntilRef = useRef<number>(0);
  // Page aspect ratio (height / width), measured from the first rendered page.
  // Used to RESERVE the height of virtualised (off-window) pages so the scroll
  // geometry stays correct — without it, ``renderMode="none"`` pages collapse
  // to 0px, the scroll area is only ~3 pages tall (can't scroll the rest), and
  // page nav / the visible-page counter break (June 2026 fix). Default ≈ A4
  // portrait until the real ratio lands a few ms later.
  const [pageRatio, setPageRatio] = useState<number>(Math.SQRT2);

  // Bytes fetch via the auth bridge (Tauri Bearer header) — same rationale
  // as before; react-pdf's own fetch path can't carry the Bearer.
  useEffect(() => {
    const ctrl = new AbortController();
    setBytes(null);
    setError(null);
    fetchSessionBytes(session.read_url, { signal: ctrl.signal })
      .then(setBytes)
      .catch((err: unknown) => {
        if ((err as { name?: string }).name === "AbortError") return;
        setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => ctrl.abort();
  }, [session.read_url]);

  const file = useMemo(() => (bytes ? { data: bytes } : null), [bytes]);

  // ResizeObserver → keep `containerWidth` in sync with the scroll container
  // minus the gutter. react-pdf's <Page width> renders at this CSS width so
  // the canvas can't overflow into the right rail.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setContainerWidth(Math.max(0, w - PAGE_GUTTER_PX));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Scroll the hinted page into view once doc reports its page count. Routed
  // through ``goToPage`` (declared below) so it uses the same reliable
  // container-scrollTo path; ``goToPageRef`` avoids a declaration-order /
  // dependency cycle.
  const goToPageRef = useRef<(n: number) => void>(() => {});
  useEffect(() => {
    if (!doc || !pageHint) return;
    goToPageRef.current(pageHint);
  }, [doc, pageHint]);

  // Page virtualisation window — only pages within ±VIRTUAL_WINDOW of
  // the currently visible page mount their canvas/text/annotation layers
  // (``renderMode="canvas"``). Pages outside the window render as
  // ``renderMode="none"`` so they reserve layout space (no scroll jump)
  // but consume zero canvas memory. Cuts memory by ~95% on 100+ page
  // PDFs and keeps scroll cadence at 60fps on macOS retina (May 25,
  // 2026 — fixes user-flagged "PDF si vede male" on large decks).
  const VIRTUAL_WINDOW = 2;

  // Observe which page is CENTRED in the viewport → drives the toolbar
  // counter. The ``-45% 0px`` rootMargin shrinks the root to a thin band at
  // the vertical centre, so exactly the page crossing the middle is
  // "intersecting" — accurate, one-page-at-a-time tracking. (The previous
  // ``400px`` band reported a page 1–2 ahead of the centred one, so ◀/▶
  // jumped multiple pages per click — June 2026 fix.) Prefetch is NOT this
  // observer's job: the ``VIRTUAL_WINDOW`` (±2 around currentPage) already
  // renders neighbours before they reach the centre.
  useEffect(() => {
    if (!doc || !scrollRef.current) return;
    const root = scrollRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!visible) return;
        // Don't fight an in-flight programmatic jump.
        if (Date.now() < suppressObserverUntilRef.current) return;
        const n = Number((visible.target as HTMLElement).dataset.pageNumber);
        if (Number.isFinite(n) && n > 0) setCurrentPage(n);
      },
      { root, rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );
    root.querySelectorAll("[data-page-number]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [doc]);

  const goToPage = useCallback(
    (n: number) => {
      const container = scrollRef.current;
      if (!container || !doc) return;
      const page = Math.max(1, Math.min(doc.numPages, n));
      // Set the page OPTIMISTICALLY first — this (a) updates the counter
      // instantly, (b) makes prev/next deterministic (they read currentPage,
      // not the IntersectionObserver's async value), and (c) expands the
      // virtualisation window so the TARGET page actually renders before we
      // land on it.
      setCurrentPage(page);
      // Hold off the scroll observer for the duration of the smooth scroll so
      // it can't bounce ``currentPage`` to a page we're scrolling THROUGH.
      suppressObserverUntilRef.current = Date.now() + 700;
      const target = container.querySelector<HTMLElement>(`[data-page-number="${page}"]`);
      if (!target) return;
      // SOTA jump (react-pdf #930): ``element.scrollIntoView()`` is unreliable
      // inside a virtualised / nested ``overflow-y-auto`` container — it can
      // pick the wrong scrollport or no-op. Compute the target's offset within
      // THIS container (reserved-height placeholders make offsetTop exact) and
      // scroll the container directly.
      const top =
        target.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
      container.scrollTo({ top, behavior: "smooth" });
    },
    [doc],
  );
  // Mirror the latest ``goToPage`` into the ref the pageHint effect calls.
  goToPageRef.current = goToPage;

  const zoomIn = useCallback(() => setZoom((z) => ZOOM_STEPS.find((s) => s > z) ?? z), []);
  const zoomOut = useCallback(
    () =>
      setZoom((z) => {
        const lower = [...ZOOM_STEPS].reverse().find((s) => s < z);
        return lower ?? z;
      }),
    [],
  );
  const zoomFit = useCallback(() => setZoom(1), []);

  // Keyboard shortcuts (May 25, 2026 — PDF SOTA pass):
  //   PgUp / PgDn / ←  / →  → prev/next page
  //   Home / End            → first / last page
  //   ⌘= / ⌘+               → zoom in
  //   ⌘-                    → zoom out
  //   ⌘0                    → fit width
  // Mounted on the scroll container itself so the shortcuts fire only
  // when the viewer has focus; they don't steal keys from the chat
  // composer or other surfaces.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !doc) return;
    const onKey = (e: KeyboardEvent) => {
      // Skip when focus is inside an editable element (text-layer
      // selection lives in the same DOM tree).
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "=" || e.key === "+")) {
        e.preventDefault();
        zoomIn();
        return;
      }
      if (mod && e.key === "-") {
        e.preventDefault();
        zoomOut();
        return;
      }
      if (mod && e.key === "0") {
        e.preventDefault();
        zoomFit();
        return;
      }
      if (e.key === "PageDown" || e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        goToPage(Math.min(doc.numPages, currentPage + 1));
        return;
      }
      if (e.key === "PageUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        goToPage(Math.max(1, currentPage - 1));
        return;
      }
      if (e.key === "Home") {
        e.preventDefault();
        goToPage(1);
        return;
      }
      if (e.key === "End") {
        e.preventDefault();
        goToPage(doc.numPages);
        return;
      }
    };
    root.addEventListener("keydown", onKey);
    return () => root.removeEventListener("keydown", onKey);
  }, [doc, currentPage, goToPage, zoomIn, zoomOut, zoomFit]);

  // Trackpad support (Sep 2026): macOS pinch-to-zoom arrives as a ``wheel``
  // event with ``ctrlKey`` set (so does ⌘/Ctrl + wheel on a mouse) — intercept
  // it for CONTINUOUS, cursor-anchored zoom; a PLAIN two-finger scroll falls
  // through to the container's native overflow pan (now both axes). The native
  // listener is ``passive:false`` so ``preventDefault`` can stop the browser's
  // own page zoom. The anchor point is applied in a layout effect once the new
  // page width has laid out, keeping the spot under the cursor fixed.
  const zoomAnchorRef = useRef<{ cx: number; cy: number; prev: number } | null>(null);
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return; // plain scroll → native pan
      e.preventDefault();
      const rect = root.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setZoom((z) => {
        zoomAnchorRef.current = { cx, cy, prev: z };
        return clampZoom(z * Math.exp(-e.deltaY * 0.0015));
      });
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    return () => root.removeEventListener("wheel", onWheel);
  }, []);
  useLayoutEffect(() => {
    const a = zoomAnchorRef.current;
    const root = scrollRef.current;
    if (!a || !root || !a.prev) return;
    const ratio = zoom / a.prev;
    // Keep the content point under the cursor stationary as the page grows.
    root.scrollLeft = (root.scrollLeft + a.cx) * ratio - a.cx;
    root.scrollTop = (root.scrollTop + a.cy) * ratio - a.cy;
    zoomAnchorRef.current = null;
  }, [zoom]);

  const onDownload = useCallback(() => {
    if (!bytes) return;
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = session.filename || "documento.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [bytes, session.filename]);

  const pageWidth = Math.max(0, containerWidth * zoom);

  return (
    <div className="flex h-full w-full flex-col bg-cream">
      {/* Toolbar — sticky chrome above the scroll area */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-hairline border-b bg-cream/95 px-4 py-2 backdrop-blur">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => goToPage(Math.max(1, currentPage - 1))}
            disabled={!doc || currentPage <= 1}
            aria-label={i18n._(msg`Pagina precedente`)}
          >
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <span className="min-w-[64px] text-center font-medium text-[11px] text-ink-2 tabular-nums">
            {doc ? `${currentPage} / ${doc.numPages}` : "—"}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => doc && goToPage(Math.min(doc.numPages, currentPage + 1))}
            disabled={!doc || currentPage >= doc.numPages}
            aria-label={i18n._(msg`Pagina successiva`)}
          >
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={zoomOut} aria-label={i18n._(msg`Riduci zoom`)}>
            <Minus className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <span className="min-w-[44px] text-center font-medium text-[11px] text-ink-2 tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <Button variant="ghost" size="sm" onClick={zoomIn} aria-label={i18n._(msg`Aumenta zoom`)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomFit}
            aria-label={i18n._(msg`Adatta alla larghezza`)}
            title={i18n._(msg`Adatta alla larghezza`)}
          >
            <Maximize2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDownload}
            disabled={!bytes}
            aria-label={i18n._(msg`Scarica PDF`)}
            title={i18n._(msg`Scarica`)}
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      </div>

      {/* Stage — fills remaining height, owns the ResizeObserver target */}
      <div ref={stageRef} className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          // ``overflow-auto`` on BOTH axes so a two-finger trackpad scroll pans
          // a zoomed drawing horizontally too (was ``overflow-x-hidden`` → you
          // could zoom but never reach the sides). At fit-width the page is
          // sized to the container, so no horizontal scrollbar appears.
          className={cn(
            "absolute inset-0 overflow-auto outline-none",
            "bg-cream focus-visible:ring-2 focus-visible:ring-ando-orange/30",
          )}
          aria-label={i18n._(
            msg`Viewer PDF — ${session.filename}. Scorri per navigare, pinch o ⌘+ / ⌘− per zoom.`,
          )}
        >
          {error && !bytes ? (
            <div role="alert" className="px-6 py-10 text-ink-3 text-sm">
              {i18n._(msg`Impossibile caricare il PDF.`)} ({error.message})
            </div>
          ) : !bytes ? (
            <div className="px-6 py-10 text-ink-3 text-sm">{i18n._(msg`Caricamento PDF…`)}</div>
          ) : pageWidth > 0 ? (
            <Document
              file={file}
              onLoadSuccess={({ numPages }) => {
                setError(null);
                setDoc({ numPages });
              }}
              onLoadError={(err) => setError(err)}
              onPassword={(callback, reason) => {
                // Brand-coherent prompt for password-protected PDFs.
                // ``reason`` is 1 = need password, 2 = wrong password
                // (per pdfjs PasswordResponses). Italian-localised copy
                // since the rest of Ando is Italian; pdfjs calls
                // ``onPassword`` again on wrong password, so we just
                // re-prompt each time and let the user cancel. May 31,
                // 2026 — migrated from ``window.prompt`` to the Ando
                // dialog system.
                const isRetry = reason === 2;
                void prompt({
                  title: i18n._(msg`PDF protetto da password`),
                  description: isRetry
                    ? i18n._(msg`Password errata. Riprova.`)
                    : i18n._(msg`Questo PDF è protetto da password. Inseriscila per aprirlo.`),
                  label: i18n._(msg`Password`),
                  inputType: "text",
                  placeholder: i18n._(msg`Password del PDF`),
                  confirmLabel: i18n._(msg`Apri`),
                }).then((pwd) => {
                  if (pwd) callback(pwd);
                });
              }}
              loading={
                <div className="px-6 py-10 text-ink-3 text-sm">{i18n._(msg`Caricamento PDF…`)}</div>
              }
              error={
                <div className="px-6 py-10 text-ink-3 text-sm">
                  {i18n._(msg`Impossibile caricare il PDF.`)}
                  {error ? ` (${error.message})` : null}
                </div>
              }
              noData={
                <div className="px-6 py-10 text-ink-3 text-sm">
                  {i18n._(msg`Nessun PDF da mostrare.`)}
                </div>
              }
            >
              {doc
                ? Array.from({ length: doc.numPages }, (_, i) => i + 1).map((pageNumber) => {
                    const isInWindow = Math.abs(pageNumber - currentPage) <= VIRTUAL_WINDOW;
                    return (
                      <div
                        key={pageNumber}
                        data-page-number={pageNumber}
                        className="mx-auto my-4 max-w-full"
                        // RESERVE the page's real height even when virtualised
                        // (off-window). ``renderMode="none"`` paints nothing and
                        // reserves no box, so without this the page collapses to
                        // 0px → broken scroll + nav. ``minHeight`` from the
                        // measured aspect ratio keeps every page's slot; the
                        // canvas fills exactly this height when it renders.
                        style={{ width: pageWidth, minHeight: pageWidth * pageRatio }}
                      >
                        <Page
                          pageNumber={pageNumber}
                          width={pageWidth}
                          // Virtualised: only pages within the window
                          // mount the canvas + text + annotation layers.
                          // Off-window pages render as ``none`` (no canvas
                          // memory) but the wrapper above reserves their height.
                          renderMode={isInWindow ? "canvas" : "none"}
                          renderTextLayer={isInWindow}
                          renderAnnotationLayer={isInWindow}
                          className="shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
                          onLoadSuccess={(page) => {
                            // Measure the real aspect ratio once a page parses
                            // (uniform-size PDFs → one value drives every
                            // placeholder). ``getViewport`` is the type-safe
                            // pdfjs API; the ratio is resolution-independent.
                            const vp = page.getViewport({ scale: 1 });
                            if (vp.width > 0) {
                              const ratio = vp.height / vp.width;
                              setPageRatio((prev) =>
                                Math.abs(prev - ratio) > 0.001 ? ratio : prev,
                              );
                            }
                          }}
                          loading={
                            <div className="grid h-full place-items-center text-[11px] text-ink-3">
                              {i18n._(msg`Pagina ${pageNumber}…`)}
                            </div>
                          }
                        />
                      </div>
                    );
                  })
                : null}
            </Document>
          ) : null}
        </div>
      </div>
    </div>
  );
}
